import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';

import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { Location } from '@angular/common';
import { AccountStateService } from '../services/account-state.service';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  isRefreshingToken = false;
  hasHandledFailedRefresh = false;
  isHandlingForbiddenAccount = false;
  private refreshTokenSubject = new BehaviorSubject<'FAILED' | string | null>(
    null,
  );
  private readonly noActiveAccountsRefreshError =
    'No active accounts available';

  tokenRefreshApiPath = '/identity/refresh';

  skippedRoutes = [
    'identity/authenticate',
    'identity/logout',
    'identity/refresh',
    'email-verification',
    'email-change/confirm',
    'oauth2/authenticate',
    'oauth2/clients',
  ];

  constructor(
    protected authService: AuthService,
    private router: Router,
    private toastService: ToastService,
    private location: Location,
    private injector: Injector,
  ) {}

  /**
   * Intercepts HTTP requests, and attempts token refresh if response is a 401. If token refresh fails, then we emit a
   * logout event so that the user can be appropriately logged out
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(this.addToken(request)).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          request.url.includes(this.tokenRefreshApiPath)
        ) {
          this.handleFailedRefresh(
            this.isInactiveSessionError(error) ? 'inactive' : 'expired',
          );
          return throwError(() => error);
        }

        if (
          error instanceof HttpErrorResponse &&
          !this.skippedRoutes.some((r) => request.url.includes(r)) &&
          error.status === 401
        ) {
          return this.handle401Error(request, next, error);
        }

        if (
          error instanceof HttpErrorResponse &&
          this.shouldHandleForbiddenAccountRequest(request, error)
        ) {
          return this.handleForbiddenAccountError(error);
        }

        return throwError(() => error);
      }),
    );
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse,
  ) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.refreshTokenSubject.next(null);

      if (localStorage.getItem(this.authService.refreshTokenLocalStorageKey)) {
        this.hasHandledFailedRefresh = false;
        return this.authService.refreshToken().pipe(
          switchMap((res) => {
            this.isRefreshingToken = false;
            this.hasHandledFailedRefresh = false;
            this.authService.setTokens(res.body);
            return from(
              this.injector
                .get(AccountStateService)
                .refreshAccountsAndReconcileSelected(
                  res.body.selectedAccountGuid,
                ),
            ).pipe(
              switchMap((accountSelection) => {
                if (accountSelection.status === 'no-active-accounts') {
                  this.handleNoActiveAccounts();
                  this.refreshTokenSubject.next('FAILED');
                  return throwError(
                    () => new Error(this.noActiveAccountsRefreshError),
                  );
                }

                this.refreshTokenSubject.next(res.body.accessToken);
                return next.handle(this.addToken(request));
              }),
            );
          }),
          catchError((error) => {
            this.isRefreshingToken = false;
            this.refreshTokenSubject.next('FAILED');
            if (
              !(error instanceof Error) ||
              error.message !== this.noActiveAccountsRefreshError
            ) {
              this.handleFailedRefresh(
                this.isInactiveSessionError(error) ? 'inactive' : 'expired',
              );
            }
            return throwError(() => error);
          }),
        );
      }

      this.isRefreshingToken = false;
      this.handleFailedRefresh(
        this.isInactiveSessionError(originalError) ? 'inactive' : 'expired',
      );
      this.refreshTokenSubject.next('FAILED');
      return throwError(() => new Error('No refresh token available'));
    }

    return this.refreshTokenSubject.pipe(
      filter((token): token is 'FAILED' | string => token !== null),
      take(1),
      switchMap((token) => {
        if (token === 'FAILED') {
          return throwError(() => new Error('Token refresh failed'));
        }

        return next.handle(this.addToken(request));
      }),
    );
  }

  private addToken(req: HttpRequest<unknown>) {
    return req.clone({
      headers: this.authService.getAuthorisationHeaders(req.headers),
    });
  }

  private handleForbiddenAccountError(
    error: HttpErrorResponse,
  ): Observable<never> {
    if (this.isHandlingForbiddenAccount) {
      return throwError(() => error);
    }

    this.isHandlingForbiddenAccount = true;

    return from(
      this.injector
        .get(AccountStateService)
        .refreshAccountsAndReconcileSelected(),
    ).pipe(
      tap((accountSelection) => {
        if (accountSelection.status === 'no-active-accounts') {
          this.handleNoActiveAccounts();
          return;
        }

        if (accountSelection.selectedAccountChanged) {
          this.toastService.info(
            'The selected account is no longer available. We switched you to another account.',
          );
          void this.router.navigate(['/admin/home']);
        }
      }),
      catchError((accountRefreshError) => {
        console.error(accountRefreshError);
        return throwError(() => error);
      }),
      finalize(() => {
        this.isHandlingForbiddenAccount = false;
      }),
      switchMap(() => throwError(() => error)),
    );
  }

  private shouldHandleForbiddenAccountRequest(
    request: HttpRequest<unknown>,
    error: HttpErrorResponse,
  ) {
    return (
      error.status === 403 &&
      !this.skippedRoutes.some((r) => request.url.includes(r)) &&
      !this.isAccountsListRequest(request)
    );
  }

  private isAccountsListRequest(request: HttpRequest<unknown>) {
    return (
      request.method === 'GET' &&
      /\/api\/v1\/accounts(?:\?|$)/.test(request.urlWithParams)
    );
  }

  private handleFailedRefresh(reason: 'expired' | 'inactive' = 'expired') {
    if (this.hasHandledFailedRefresh) {
      return;
    }

    this.hasHandledFailedRefresh = true;
    this.authService.clearAuthState();
    this.toastService.error(
      reason === 'inactive'
        ? 'Your account is no longer active. Please contact support if you believe this is unexpected.'
        : 'Session Expired. Please log in again',
    );
    const redirectUrl = this.router.parseUrl('/');
    redirectUrl.queryParams['returnUrl'] = this.location.path();
    void this.router.navigateByUrl(redirectUrl);
  }

  private handleNoActiveAccounts() {
    this.authService.clearAuthState();
    this.toastService.error(
      'Your account is no longer active. Please contact support if you believe this is unexpected.',
    );
    const redirectUrl = this.router.parseUrl('/');
    redirectUrl.queryParams['returnUrl'] = this.location.path();
    void this.router.navigateByUrl(redirectUrl);
  }

  private isInactiveSessionError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return this.getErrorText(error.error).some((message) => {
      const normalizedMessage = message.toLowerCase();
      return (
        normalizedMessage.includes('inactive') ||
        normalizedMessage.includes('deactivated') ||
        normalizedMessage.includes('no longer active')
      );
    });
  }

  private getErrorText(value: unknown): string[] {
    if (typeof value === 'string') {
      return [value];
    }

    if (Array.isArray(value)) {
      return value.flatMap((entry) => this.getErrorText(entry));
    }

    if (!value || typeof value !== 'object') {
      return [];
    }

    return Object.values(value as Record<string, unknown>).flatMap((entry) =>
      this.getErrorText(entry),
    );
  }
}
